import { ArrowRight, Loader2 } from "lucide-react";

const INTERVAL_LABEL = { last_7d: "7D", last_30d: "30D", ytd: "YTD" };

const fmtPct = (n) => {
  if (n == null || isNaN(n)) return "—";
  const s = n >= 0 ? "+" : "";
  return `${s}${n.toFixed(2)}%`;
};

const fmtPrice = (n, currency = "USD") => {
  if (n == null || isNaN(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
};

function PctCell({ v }) {
  if (v == null) {
    return <td className="text-right px-3 py-3 font-mono text-zinc-600">—</td>;
  }
  const up = v >= 0;
  return (
    <td
      className={`text-right px-3 py-3 font-mono ${up ? "text-[#00E676]" : "text-[#FF3B30]"}`}
    >
      {fmtPct(v)}
    </td>
  );
}

export default function WatchlistDigestTable({ digest, loading, onPickTicker }) {
  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-zinc-500 text-sm">
        <Loader2 size={14} className="animate-spin mr-2" /> Computing digest…
      </div>
    );
  }
  if (!digest || digest.rows.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-zinc-500">
        Add tickers above to see weekly digest.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="digest-table">
        <thead>
          <tr className="border-b border-zinc-900 bg-[#0e0e10]">
            <th className="text-label text-left px-5 py-3 font-normal">Ticker</th>
            <th className="text-label text-right px-3 py-3 font-normal">Last Price</th>
            {digest.intervals.map((iid) => (
              <th key={iid} colSpan={2} className="text-label text-right px-3 py-3 font-normal">
                {INTERVAL_LABEL[iid]}
              </th>
            ))}
            <th className="px-3 py-3 w-12"></th>
          </tr>
          <tr className="border-b border-zinc-900 bg-[#0e0e10]">
            <th></th>
            <th></th>
            {digest.intervals.map((iid) => (
              <>
                <th
                  key={`${iid}-tr`}
                  className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest text-right px-3 py-1 font-normal"
                >
                  TR
                </th>
                <th
                  key={`${iid}-drip`}
                  className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest text-right px-3 py-1 font-normal"
                  title="DRIP = reinvested total return"
                >
                  DRIP
                </th>
              </>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {digest.rows.map((r) => {
            const by = Object.fromEntries((r.intervals || []).map((x) => [x.interval_id, x]));
            return (
              <tr
                key={r.symbol}
                className="border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/30"
              >
                <td className="px-5 py-3">
                  <div className="font-mono font-bold">{r.symbol}</div>
                  <div className="text-[11px] text-zinc-500 truncate max-w-[220px]">{r.name}</div>
                </td>
                <td className="text-right px-3 py-3 font-mono text-zinc-300">
                  {fmtPrice(r.last_price, r.currency)}
                </td>
                {digest.intervals.map((iid) => (
                  <>
                    <PctCell key={`${iid}-tr`} v={by[iid]?.total_return_pct} />
                    <PctCell key={`${iid}-drip`} v={by[iid]?.total_return_reinvest_pct} />
                  </>
                ))}
                <td className="px-3 py-3">
                  <button
                    onClick={() => onPickTicker?.(r.symbol)}
                    className="text-zinc-500 hover:text-white p-1"
                    title="Open"
                  >
                    <ArrowRight size={14} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
